import { useEffect, useRef, useState } from "react";
import { Camera, RefreshCw, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const W = 480, H = 360;

function stamp(ctx, label) {
  ctx.fillStyle = "rgba(37,14,49,.75)";
  ctx.fillRect(0, H - 34, W, 34);
  ctx.fillStyle = "#D4AF37";
  ctx.font = "bold 14px sans-serif";
  ctx.fillText(`${label} · ${new Date().toLocaleString("en-GB")}`, 10, H - 12);
}

export default function CameraCapture({ open, onOpenChange, label, onConfirm, busy }) {
  const video = useRef(null);
  const stream = useRef(null);
  const [photo, setPhoto] = useState(null);
  const [camError, setCamError] = useState("");
  const [coords, setCoords] = useState(null);

  useEffect(() => {
    if (!open) return;
    setPhoto(null); setCamError("");
    navigator.geolocation?.getCurrentPosition((p) => setCoords({ lat: p.coords.latitude, lng: p.coords.longitude }), () => setCoords(null));
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: "user", width: W, height: H } })
      .then((s) => { stream.current = s; if (video.current) video.current.srcObject = s; })
      .catch(() => setCamError("Camera not available. Take or upload a photo instead."));
    return () => stream.current?.getTracks().forEach((t) => t.stop());
  }, [open]);

  const draw = (source) => {
    const c = document.createElement("canvas");
    c.width = W; c.height = H;
    const ctx = c.getContext("2d");
    ctx.drawImage(source, 0, 0, W, H);
    stamp(ctx, label);
    setPhoto(c.toDataURL("image/jpeg", 0.65));
  };

  const fromFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const img = new Image();
    img.onload = () => draw(img);
    img.src = URL.createObjectURL(f);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="camera-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{label} - photo evidence</DialogTitle>
          <DialogDescription>Your photo, time and location are saved to the staff book.</DialogDescription>
        </DialogHeader>
        <div className="relative rounded-xl overflow-hidden bg-[#250E31] aspect-[4/3]">
          {photo ? <img src={photo} alt="Captured" className="w-full h-full object-cover" data-testid="camera-preview" />
            : camError ? <div className="h-full grid place-items-center text-white/80 text-sm p-6 text-center" data-testid="camera-error">{camError}</div>
            : <video ref={video} autoPlay playsInline muted className="w-full h-full object-cover -scale-x-100" data-testid="camera-video" />}
        </div>
        <div className="text-xs text-[#6b5a73]" data-testid="camera-location">
          {coords ? `Location: ${coords.lat.toFixed(4)}, ${coords.lng.toFixed(4)}` : "Location not shared"}
        </div>
        <div className="flex flex-wrap gap-2">
          {!photo && !camError && <button className="dx-btn-purple flex-1" onClick={() => draw(video.current)} data-testid="camera-snap-btn"><Camera className="w-4 h-4" />Take photo</button>}
          {photo && <button className="dx-btn-ghost" onClick={() => setPhoto(null)} data-testid="camera-retake-btn"><RefreshCw className="w-4 h-4" />Retake</button>}
          <label className="dx-btn-ghost cursor-pointer" data-testid="camera-upload-label">
            <Upload className="w-4 h-4" />Upload
            <input type="file" accept="image/*" capture="user" className="hidden" onChange={fromFile} data-testid="camera-file-input" />
          </label>
          {photo && <button className="dx-btn-gold flex-1" disabled={busy} onClick={() => onConfirm(photo, coords)} data-testid="camera-confirm-btn">{busy ? "Saving…" : `Confirm ${label}`}</button>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
